import React, {useState, useRef} from "react";
import {useTranslation} from "react-i18next";
import {FaMicrophone, FaCircle} from "react-icons/fa";
import {ImSpinner8} from "react-icons/im";
import lamejs from "lamejstmp";
import Mixpanel from "../utils/mixpanel";

function AudioRecorder({
    onRecordingComplete,
    onRecordingStart,
    onDelete,
}: {
    onRecordingComplete: (audioFile: File) => void;
    onRecordingStart: () => void;
    onDelete: () => void;
}) {
    const {t} = useTranslation("global");
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);

    const convertToMp3 = async (audioBlob: Blob): Promise<Blob> => {
        const audioContext = new AudioContext();
        const arrayBuffer = await audioBlob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const channels = audioBuffer.numberOfChannels;
        const sampleRate = audioBuffer.sampleRate;
        const mp3encoder = new lamejs.Mp3Encoder(channels, sampleRate, 128);
        const samples = new Int16Array(audioBuffer.length * channels);

        // Convert to required format
        const left = audioBuffer.getChannelData(0);
        const right = channels > 1 ? audioBuffer.getChannelData(1) : left;

        // Fix: Process samples correctly for mono or stereo
        for (let i = 0; i < audioBuffer.length; i++) {
            if (channels === 1) {
                samples[i] = left[i] * 0x7fff;
            } else {
                samples[i * 2] = left[i] * 0x7fff;
                samples[i * 2 + 1] = right[i] * 0x7fff;
            }
        }

        const mp3Data: Int8Array[] = [];
        const blockSize = channels === 1 ? 576 : 1152; // Fix: Adjust block size based on channels

        for (let i = 0; i < samples.length; i += blockSize) {
            const sampleChunk = samples.subarray(i, i + blockSize);
            const mp3buf =
                channels === 1
                    ? mp3encoder.encodeBuffer(sampleChunk)
                    : mp3encoder.encodeBuffer(
                          sampleChunk.subarray(0, blockSize / 2),
                          sampleChunk.subarray(blockSize / 2)
                      );
            if (mp3buf.length > 0) {
                mp3Data.push(mp3buf);
            }
        }

        const end = mp3encoder.flush();
        if (end.length > 0) {
            mp3Data.push(end);
        }

        return new Blob(mp3Data, {type: "audio/mp3"});
    };

    const startRecording = async () => {
        onRecordingStart();
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: "audio/webm;codecs=opus",
            });

            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                setIsProcessing(true);
                try {
                    const webmBlob = new Blob(chunksRef.current, {
                        type: "audio/webm",
                    });
                    const mp3Blob = await convertToMp3(webmBlob);

                    const fileName = `${t(
                        "recorder.name"
                    )}_${getFormattedDateTime()}.mp3`;
                    const audioFile = new File([mp3Blob], fileName, {
                        type: "audio/mp3",
                        lastModified: Date.now(),
                    });

                    onRecordingComplete(audioFile);
                } catch (error) {
                    console.error("Error processing audio:", error);
                    Mixpanel.track("Error processing audio", {
                        error: error,
                    });
                } finally {
                    setIsProcessing(false);
                    chunksRef.current = [];
                }
            };

            mediaRecorder.start(100);
            setIsRecording(true);
        } catch (error) {
            console.error("Error accessing microphone:", error);
            Mixpanel.track("Error accessing microphone", {
                error: error,
            });
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            mediaRecorderRef.current.stream
                .getTracks()
                .forEach((track) => track.stop());
        }
    };

    const getFormattedDateTime = () => {
        const now = new Date();
        return now.toISOString().replace(/[:.]/g, "-");
    };

    return (
        <div className="w-full flex flex-row justify-between items-center mt-[12px]">
            {!isProcessing && (
                <div className="w-full flex justify-center">
                    {!isRecording ? (
                        <button
                            onClick={startRecording}
                            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white border-[1px] border-primary rounded-[6px] text-primary"
                        >
                            <FaMicrophone className="text-[16px]" />
                            <p className="text-[20px] font-semibold leading-[28px] tracking-[0.3px]">
                                {t("recorder.start")}
                            </p>
                        </button>
                    ) : (
                        <button
                            onClick={stopRecording}
                            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white border-[1px] border-primary rounded-[6px] text-primary"
                        >
                            <FaCircle className="text-[16px] animate-pulse" />
                            <p className="text-[20px] font-semibold leading-[28px] tracking-[0.3px]">
                                {t("recorder.stop")}
                            </p>
                            {/*  <div className="absolute -right-8 top-1/2 -translate-y-1/2 w-5 h-5 bg-red-500 rounded-full animate-pulse" />*/}
                        </button>
                    )}
                </div>
            )}

            {isProcessing && (
                <div className="w-full flex items-center justify-center gap-3 mt-4">
                    <ImSpinner8 className="animate-spin text-gray-600 text-xl" />
                    <span className="text-gray-600">Processing...</span>
                </div>
            )}
        </div>
    );
}

export default AudioRecorder;
